// frontend/src/components/common/CameraModal.js
//
// Reusable live-camera capture modal for Everything Automotive (web/React).
//
// HISTORY
//   The original camera here was unreliable. This is a rebuild on top of the
//   proven ScienceMindset live-camera engine, which snaps and uploads on the
//   spot. The OUTER prop interface is unchanged (isOpen / onClose / onCapture /
//   viewType), so every page that already uses <CameraModal /> keeps working
//   with no changes.
//
// HOW IT WORKS
//   • Opens the device camera via the browser MediaDevices API (getUserMedia).
//     A standard web capability that works in mobile Chrome / Safari with NO
//     native app, as long as the page is served over HTTPS (Render provides it)
//     or on http://localhost in dev.
//   • Prefers the REAR camera (facingMode: "environment") so a seller can point
//     the phone straight at the part / vehicle. A flip button toggles to selfie
//     (needed for the face_verification slot).
//   • Tapping the shutter draws the current video frame onto a <canvas>, then
//     converts it to a JPEG File via canvas.toBlob(). That File is handed back
//     to the parent through onCapture(file) — the SAME shape the <input> file
//     picker produces, so it flows through the existing upload logic unchanged.
//
// PROPS
//   isOpen     bool      show / hide the modal
//   onClose    fn()      called when the modal is dismissed
//   onCapture  fn(file)  called once per accepted photo with a JPEG File
//   viewType   string    slot label, e.g. "front", "engine", "waybill_receipt"

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaCamera, FaTimes, FaSyncAlt, FaCheck, FaRedo, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';

function CameraModal({ isOpen, onClose, onCapture, viewType = 'image' }) {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);

    const [facingMode, setFacingMode] = useState('environment'); // rear by default
    const [ready, setReady] = useState(false);
    const [starting, setStarting] = useState(false);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);   // { url, file } shown after a snap
    const [captured, setCaptured] = useState(0);     // how many added this session

    // A friendly label from the viewType, e.g. "waybill_receipt" -> "Waybill Receipt".
    const niceLabel = String(viewType || 'image')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());

    // Browsers only expose the camera on a secure origin (HTTPS) or localhost.
    const isSecure = typeof window !== 'undefined'
        && (window.isSecureContext
            || window.location.hostname === 'localhost'
            || window.location.hostname === '127.0.0.1');

    const stopStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
        setReady(false);
    }, []);

    const startStream = useCallback(async (mode) => {
        setError(null);
        setStarting(true);
        setReady(false);
        stopStream();

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setError('This browser does not support live camera capture. Please update your browser or use the Browse option to pick a photo.');
            setStarting(false);
            return;
        }
        if (!isSecure) {
            setError('The camera can only be opened over a secure (HTTPS) connection. Open the site via its https:// address, then try again.');
            setStarting(false);
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: mode },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                // iOS Safari needs an explicit play() after the stream is attached.
                await videoRef.current.play().catch(() => {});
            }
            setReady(true);
        } catch (err) {
            let msg = 'Could not access the camera.';
            if (err && (err.name === 'NotAllowedError' || err.name === 'SecurityError')) {
                msg = 'Your browser blocked camera access. Tap the lock or \u22ee menu in your browser\'s address bar and set Camera to "Allow", then tap Try again. If you can\'t find it, open this page in the Opera browser — it grants camera permission with a single tap.';
            } else if (err && err.name === 'NotFoundError') {
                msg = 'No camera was found on this device.';
            } else if (err && err.name === 'NotReadableError') {
                msg = 'The camera is already in use by another app. Close it and try again.';
            }
            setError(msg);
        } finally {
            setStarting(false);
        }
    }, [isSecure, stopStream]);

    // Open / close lifecycle.
    useEffect(() => {
        if (isOpen) {
            startStream(facingMode);
        } else {
            stopStream();
            setPreview(null);
            setCaptured(0);
            setError(null);
        }
        return () => stopStream();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const flipCamera = () => {
        const next = facingMode === 'environment' ? 'user' : 'environment';
        setFacingMode(next);
        startStream(next);
    };

    const takePhoto = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas || !ready) return;

        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) return;

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, w, h);

        canvas.toBlob((blob) => {
            if (!blob) {
                setError('Failed to capture the photo. Please try again.');
                return;
            }
            const stamp = new Date().toISOString().replace(/[:.]/g, '-');
            const file = new File([blob], `${viewType}_${stamp}.jpg`, { type: 'image/jpeg' });
            setPreview({ url: URL.createObjectURL(blob), file });
        }, 'image/jpeg', 0.92);
    };

    const retake = () => {
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
    };

    const acceptPhoto = () => {
        if (preview?.file && onCapture) onCapture(preview.file);
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
        setCaptured(c => c + 1);
        // Close after a successful upload-now: each slot takes one image. The
        // parent decides what to do next; reopening is one tap.
        handleClose();
    };

    const handleClose = () => {
        stopStream();
        if (preview?.url) URL.revokeObjectURL(preview.url);
        setPreview(null);
        if (onClose) onClose();
    };

    if (!isOpen) return null;

    const modal = (
        <div
            className="fixed inset-0 z-[9999] bg-black/95 flex flex-col"
            role="dialog"
            aria-modal="true"
            style={{
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
            }}
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 text-white shrink-0">
                <span className="font-black uppercase tracking-widest text-xs md:text-sm flex items-center gap-2">
                    <FaCamera /> Snap {niceLabel}
                    {captured > 0 && (
                        <span className="ml-1 bg-green-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                            {captured} added
                        </span>
                    )}
                </span>
                <button
                    type="button"
                    onClick={handleClose}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 transition-colors"
                    aria-label="Close camera"
                >
                    <FaTimes />
                </button>
            </div>

            {/* Permission hint — always visible so sellers aren't stuck on a blocked prompt. */}
            <div className="px-4 pb-2 shrink-0">
                <p className="flex items-start gap-2 text-sm md:text-base text-white/80 leading-snug max-w-md mx-auto text-center justify-center">
                    <FaInfoCircle className="mt-1 shrink-0 text-white/60" />
                    <span>Asked to allow the camera? Tap <b>Allow</b>. If your browser keeps blocking it, open this page in the <b>Opera</b> browser — it allows the camera in one tap.</span>
                </p>
            </div>

            {/* Stage */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden min-h-0">
                {error ? (
                    <div className="text-center text-white px-6 max-w-sm">
                        <FaExclamationTriangle className="text-4xl text-yellow-400 mx-auto mb-4" />
                        <p className="font-bold text-sm leading-relaxed">{error}</p>
                        {isSecure && (
                            <button
                                type="button"
                                onClick={() => startStream(facingMode)}
                                className="mt-5 px-5 py-3 rounded-2xl bg-white text-gray-900 font-black uppercase tracking-widest text-xs"
                            >
                                Try again
                            </button>
                        )}
                    </div>
                ) : preview ? (
                    <img src={preview.url} alt="Captured" className="max-h-full max-w-full object-contain" />
                ) : (
                    <>
                        <video
                            ref={videoRef}
                            playsInline
                            muted
                            autoPlay
                            className="max-h-full max-w-full object-contain"
                        />
                        {(starting || !ready) && (
                            <div className="absolute inset-0 flex items-center justify-center text-white/80 font-bold text-sm">
                                Starting camera…
                            </div>
                        )}
                        {/* Framing guide */}
                        {ready && (
                            <div className="pointer-events-none absolute inset-6 md:inset-12 border-2 border-white/40 rounded-2xl" />
                        )}
                    </>
                )}
                <canvas ref={canvasRef} className="hidden" />
            </div>

            {/* Controls */}
            <div className="px-6 py-5 md:py-8 shrink-0">
                {preview ? (
                    <div className="max-w-md mx-auto space-y-3">
                        <div className="flex items-center justify-center gap-3 md:gap-4">
                            <button
                                type="button"
                                onClick={retake}
                                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-white/15 text-white font-black uppercase tracking-widest text-xs hover:bg-white/25 transition-colors"
                            >
                                <FaRedo /> Retake
                            </button>
                            <button
                                type="button"
                                onClick={acceptPhoto}
                                className="flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl bg-green-600 text-white font-black uppercase tracking-widest text-xs hover:bg-green-700 transition-colors"
                            >
                                <FaCheck /> Upload now
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between max-w-md mx-auto">
                        <button
                            type="button"
                            onClick={flipCamera}
                            disabled={!ready}
                            className="w-12 h-12 flex items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/30 transition-colors disabled:opacity-40"
                            aria-label="Switch camera"
                        >
                            <FaSyncAlt />
                        </button>

                        {/* Shutter */}
                        <button
                            type="button"
                            onClick={takePhoto}
                            disabled={!ready}
                            className="rounded-full bg-white flex items-center justify-center shadow-lg disabled:opacity-40 active:scale-95 transition-transform"
                            style={{ width: '4.5rem', height: '4.5rem' }}
                            aria-label="Take photo"
                        >
                            <span className="w-14 h-14 rounded-full border-4 border-gray-900" />
                        </button>

                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 h-12 flex items-center justify-center rounded-full bg-white/15 text-white font-black uppercase tracking-widest text-[10px] hover:bg-white/30 transition-colors"
                        >
                            Done
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    // Render into document.body so the modal escapes any parent stacking context
    // (transforms/opacity on ancestors) and sits above the navbar and bottom nav.
    return createPortal(modal, document.body);
}

export default CameraModal;
